// File: src/pages/seller/AddProduct/sections/TechnicalSpecifications.jsx
import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  CRow,
  CCol,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import HazardModal from "../../../../components/seller/HazardModal";
import { useParams } from "react-router-dom";
import { formatWithCommasAndDecimals, parseRawNumber } from "../../../../utils/price";

const TechnicalSpecifications = ({ isMetal = true }) => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const hazard = watch("hazard_types") || [];
  const hazardDesc = watch("hazard_description");

  const [modalVisible, setModalVisible] = useState(false);
  const [hazardDetails, setHazardDetails] = useState("");

  useEffect(() => {
    if (isEditMode && hazard.includes("hazardous") && hazardDesc) {
      setHazardDetails(hazardDesc);
    }
  }, [isEditMode, hazard, hazardDesc]);

  const handleSave = () => {
    setValue("hazard_description", hazardDetails || "");
    setModalVisible(false);
  };

  const handleModalClose = () => {
    // Close modal
    setModalVisible(false);

    // If hazardous is selected but user didn't save → uncheck it
    if (hazard.includes("hazardous") && !hazardDetails) {
      setValue("hazard_types", []); // uncheck both
      setValue("hazard_description", ""); // clear description
    }
  };

  const handleEditClick = () => {
    // Open the modal when clicking "Edit" if hazard description exists
    if (hazardDesc) {
      setHazardDetails(hazardDesc);
      setModalVisible(true);
    }
  };

  const handleHazardCheckbox = (optionId, checked) => {
    // If 'checked' is true, update the hazard array to only include the selected checkbox
    if (checked) {
      setValue("hazard_types", [optionId], { shouldValidate: true }); // Set only the selected checkbox
    } else {
      // If unchecked, remove it from the hazard array
      setValue(
        "hazard_types",
        hazard.filter((h) => h !== optionId),
        { shouldValidate: true }
      );
    }

    if (optionId === "hazardous" && checked) {
      // Open modal if no description exists
      if (!hazardDesc) {
        setHazardDetails("");
        setModalVisible(true);
      }
    }

    if (!checked && optionId === "hazardous") {
      // Clearing hazard description when unchecked
      setValue("hazard_description", "");
      setHazardDetails("");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        padding: "1.5rem",
        position: "relative",
        marginBottom: "1.5rem",
      }}
    >
      {/* Title with overlapping border */}
      <span
        style={{
          position: "absolute",
          top: "-0.8rem",
          left: "1rem",
          backgroundColor: "white",
          padding: "0 0.5rem",
          fontWeight: "600",
          color: "#333",
          fontSize: "1rem",
        }}
      >
        Technical Specifications
      </span>

      {/* First Row: Quantity, Unit, Sorting Type, Weight (commented) */}
      <CRow className="mb-3">
        {/* Quantity Field moved to first column */}
        <CCol md={3}>
          <CFormLabel>Quantity</CFormLabel>
          <Controller
            name="quantity"
            control={control}
            rules={{
              validate: (value) =>
                !value ||
                /^\d+(\.\d{0,3})?$/.test(value) ||
                "Up to 3 decimal places allowed",
            }}
            render={({ field }) => (
              <CFormInput
                {...field}
                type="text"
                inputMode="decimal"
                placeholder="Enter quantity"
                value={formatWithCommasAndDecimals(field.value)}
                onChange={(e) => {
                  const raw = parseRawNumber(e.target.value);

                  if (/^\d*(\.\d{0,3})?$/.test(raw)) {
                    field.onChange(raw);
                  }
                }}
              />
            )}
          />
          {errors.quantity && (
            <div style={{ color: "red" }}>{errors.quantity.message}</div>
          )}
        </CCol>

        <CCol md={3}>
          <CFormLabel>Unit Type</CFormLabel>
          <Controller
            name="unit_of_measurement"
            control={control}
            rules={{ required: "Unit is required" }}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "Troy Ounce", value: "toz" },
                  { label: "Metric Tonne", value: "mt" },
                  { label: "Kg", value: "kg" },
                  { label: "gram", value: "g" },
                ]}
              />
            )}
          />
          {errors.unit_of_measurement && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.unit_of_measurement.message}
            </div>
          )}
        </CCol>

        <CCol md={3}>
          <CFormLabel>Sorting Type</CFormLabel>
          <Controller
            name="sorting_type"
            control={control}
            rules={{ required: "Sorting type is required" }}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "Sorted", value: "sorted" },
                  { label: "Unsorted", value: "unsorted" },
                ]}
              />
            )}
          />
          {errors.sorting_type && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.sorting_type.message}
            </div>
          )}
        </CCol>

        {/* Attachment moved to first column in last row */}
        <CCol md={3}>
          <CFormLabel>Attachment (Waste Type)</CFormLabel>
          <CFormInput
            type="text"
            {...register("attachment")}
            placeholder="e.g. Plastic residue etc."
          />
          {errors.attachment && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.attachment.message}
            </div>
          )}
        </CCol>
      </CRow>

      {/* Second Row: Waste %, Toxic Level, Attachment at first column, Hazard at the end */}
      <CRow className="mb-3">
        <CCol md={3}>
          <CFormLabel>Waste % Range</CFormLabel>
          <Controller
            name="waste_range"
            control={control}
            rules={{ required: "Waste range is required" }}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "0–2%", value: "0-2" },
                  { label: "2–5%", value: "2-5" },
                  { label: "5–10%", value: "5-10" },
                  { label: "10%+", value: "10+" },
                ]}
              />
            )}
          />
          {errors.waste_range && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.waste_range.message}
            </div>
          )}
        </CCol>

        <CCol md={3}>
          <CFormLabel>Hazard Declaration</CFormLabel>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {[
              { id: "non-hazardous", label: "Non-Hazardous" },
              { id: "hazardous", label: "Hazardous" },
            ].map((option) => (
              <CFormCheck
                key={option.id}
                id={option.id}
                label={option.label}
                checked={hazard.includes(option.id)}
                onChange={(e) =>
                  handleHazardCheckbox(option.id, e.target.checked)
                }
              />
            ))}

            {/* Add this conditional hyperlink */}
            {hazard.includes("hazardous") && hazardDesc && (
              <button
                type="button"
                className="btn btn-link p-0"
                style={{
                  fontSize: "0.9rem",
                  textDecoration: "underline",
                  color: "#0d6efd",
                }}
                onClick={handleEditClick}
              >
                Edit details
              </button>
            )}
          </div>
          {errors.hazard_types && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.hazard_types.message}
            </div>
          )}
        </CCol>

        {/* Commented out for future use: Toxic level and Weight */}
        {/* 
        <CCol md={3}>
          <CFormLabel>Toxic Level</CFormLabel>
          <Controller
            name="toxic_level"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "Low", value: "Low" },
                  { label: "Medium", value: "Medium" },
                  { label: "High", value: "High" },
                ]}
              />
            )}
          />
        </CCol>

        <CCol md={3}>
          <CFormLabel>Weight (kg)</CFormLabel>
          <CFormInput type="number" {...register("weight")} min={0} />
          {errors.weight && (
            <div style={{ color: "red" }}>{errors.weight.message}</div>
          )}
        </CCol>
        */}
      </CRow>

      <HazardModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSave={handleSave}
        textValue={hazardDetails}
        onTextChange={setHazardDetails}
      />
    </div>
  );
};

export default React.memo(TechnicalSpecifications);

{
  /* <CCol md={3}>
          <CFormLabel>Toxic Level</CFormLabel>
          <Controller
            name="toxic_level"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "Low", value: "Low" },
                  { label: "Medium", value: "Medium" },
                  { label: "High", value: "High" },
                ]}
              />
            )}
          />
        </CCol> */
}

{
  /* Hazard Type moved to last column */
}

{
  /*
        <CCol md={3}>
          <CFormLabel>Weight (kg)</CFormLabel>
          <CFormInput type="number" {...register("weight")} min={0} />
          {errors.weight && (
            <div style={{ color: "red" }}>{errors.weight.message}</div>
          )}
        </CCol>
        */
}
